import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Clock, TrendingUp } from "lucide-react";
import { api, formatApiError } from "../../api";
import { CONTROL } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";
import { useCelebration } from "../../contexts/CelebrationContext";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";

// DEFECT-D-20 #3/#4: active trials + the ones expiring within 14 days, with a one-click
// "Convert to paid" action right where the risk (an expiring trial not yet converted) is seen —
// no dead-end KPI tile.
export function TrialsPanel({ activeTrials, conversionRate, conversionNote, loading }) {
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const { celebrate } = useCelebration();
  const convertMutation = useMutation({
    mutationFn: (orgId) => api.post(`/control/gyms/${orgId}/convert-trial`),
    onSuccess: () => { celebrate("trial_converted"); queryClient.invalidateQueries({ queryKey: ["platform-summary"] }); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  if (loading || !activeTrials) {
    return <div className="solid-surface rounded-[14px] p-5 h-56 animate-pulse" data-testid={CONTROL.trialsPanel} />;
  }

  return (
    <motion.div {...fadeUp(reducedMotion, 0.06)} className="solid-surface rounded-[14px] p-5" data-testid={CONTROL.trialsPanel}>
      <div className="flex items-baseline justify-between mb-3">
        <p className="eyebrow">Trials ({activeTrials.count} active)</p>
        <span className="text-xs font-semibold flex items-center gap-1" style={{ color: "var(--scarlet-text-secondary)" }}>
          <TrendingUp size={12} /> {conversionRate != null ? `${conversionRate}% converted` : "No history yet"}
        </span>
      </div>
      {conversionNote && <p className="text-xs mb-3" style={{ color: "var(--scarlet-text-secondary)" }}>{conversionNote}</p>}
      {activeTrials.expiring_soon.length === 0 ? (
        <p className="text-sm py-4" style={{ color: "var(--scarlet-text-secondary)" }}>No trials expiring in the next 14 days.</p>
      ) : (
        <div className="space-y-1.5">
          {activeTrials.expiring_soon.map((g) => (
            <div key={g.organization_id} className="flex items-center justify-between gap-3 px-2 py-1.5 rounded-[8px]">
              <Link to={`/kairoo/gyms/${g.organization_id}`} className="flex items-center gap-2 min-w-0 hover:underline">
                <Clock size={14} className="shrink-0" style={{ color: g.days_left <= 3 ? "var(--scarlet-neg)" : "#D97706" }} />
                <span className="text-sm truncate" style={{ color: "var(--scarlet-text)" }}>{g.display_name}</span>
                <span className="text-xs shrink-0" style={{ color: "var(--scarlet-text-secondary)" }}>{g.days_left}d left</span>
              </Link>
              <Button
                size="sm" variant="outline" data-testid={CONTROL.trialConvertButton(g.organization_id)}
                disabled={convertMutation.isPending} onClick={() => convertMutation.mutate(g.organization_id)}
              >
                Convert to paid
              </Button>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}
